/**
 * One number this line refuses to answer.
 *
 * The row leads with the name when we know one, exactly as the do-not-call list
 * does, but for the opposite reason. There, a name says who asked us to stop.
 * Here, a name is the thing that should not be there: a blocked number that
 * belongs to a client or a lead is almost certainly a mistake, and this row is
 * the only place in the CRM where that mistake is visible at all.
 *
 * So the warning is drawn beside the name rather than hidden in a tooltip, and
 * Unblock sits directly under it in the menu. Somebody who spots the badge
 * should not have to go anywhere to undo it.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, Copy, MoreHorizontal, PhoneOff, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { InboundBlock } from "../../../../lib/callBlocklist";
import { formatPhone } from "../../../../lib/phone";
import { Pop, useClosePops } from "../../../shell/Pop";
import { StatusPill, fmtStamp, useToast } from "../../../ui";
import { blocksKnownParty, sourceTone } from "./rows";

export function BlocklistRow({
  row,
  cols,
  canRemove,
  onRemove,
}: {
  row: InboundBlock;
  /** The grid tracks, shared with the head so the two cannot drift apart. */
  cols: string;
  /**
   * One right, not two, and that is the difference from the do-not-call row.
   * There, removing is the direction that can do harm, so it wants the wider
   * key. Here the harm is the entry itself - a customer nobody can reach - and
   * the cure may not be locked behind a key the operator does not hold.
   */
  canRemove: boolean;
  onRemove: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  const party = row.party;
  const lastCallId = party?.lastCallId ?? null;
  const known = blocksKnownParty(row);

  async function copyValue() {
    close();
    try {
      await navigator.clipboard.writeText(row.value);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    <div className="crm-list-row" style={{ gridTemplateColumns: cols }}>
      <span className="flex min-w-0 items-center gap-3">
        <span className={cn("crm-av", known ? "crm-av-warn" : "crm-av-soft")}>
          {known ? <AlertTriangle className="size-4" /> : <PhoneOff className="size-4" />}
        </span>
        <span className="min-w-0">
          <span className="crm-prow-n crm-trunc block">
            {party?.companyName ?? t("calls.blocklist.unknownParty")}
          </span>
          <span className="crm-prow-s crm-tnum crm-trunc block">{formatPhone(row.value)}</span>
        </span>
      </span>

      <span>
        <StatusPill tone={sourceTone(row.source)}>{t(`calls.blocklist.source.${row.source}`)}</StatusPill>
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">{row.note ?? row.reason ?? "-"}</span>

      {/* How many calls we have on record with this number, and the way into the
          newest of them. This is the evidence behind the entry: an operator
          asked "why is this blocked" wants the call, not the count. */}
      <span className="crm-mut crm-tnum text-[12.5px]">
        {party && party.calls > 0 ? (
          lastCallId ? (
            <Link to={`/calls/${lastCallId}`} className="text-primary hover:underline">
              {t("calls.blocklist.callCount", { n: party.calls })}
            </Link>
          ) : (
            t("calls.blocklist.callCount", { n: party.calls })
          )
        ) : (
          t("calls.blocklist.noCalls")
        )}
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">
        {row.createdByName ? `${row.createdByName} · ` : ""}
        {fmtStamp(row.createdAt)}
      </span>

      <span className="justify-self-end">
        <Pop
          trigger={(open) => (
            <button
              type="button"
              className={cn("crm-cb", open && "is-on")}
              title={t("common.more")}
              aria-label={t("common.more")}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreHorizontal className="size-4" />
            </button>
          )}
        >
          {/* Said at the top of the menu, not only on the badge. The person who
              opens this menu is deciding whether to undo the entry, and that is
              the sentence they need in front of them while they decide. */}
          {known ? (
            <div className="crm-menu-head">
              <div className="crm-menu-s">{t("calls.blocklist.knownPartyHint")}</div>
            </div>
          ) : null}

          <button type="button" className="crm-mi" onClick={() => void copyValue()}>
            <Copy className="size-4" />
            {t("calls.blocklist.copyValue")}
          </button>

          {canRemove ? (
            <>
              <div className="crm-menu-sep" />
              {/* Not `crm-mi-danger`. Unblocking is the SAFE direction here -
                  it puts a caller back in touch - and painting it red would
                  make the cautious reader leave a blocked customer blocked. */}
              <button
                type="button"
                className="crm-mi"
                onClick={() => {
                  close();
                  onRemove();
                }}
              >
                <Trash2 className="size-4" />
                {t("calls.blocklist.remove")}
              </button>
            </>
          ) : null}
        </Pop>
      </span>
    </div>
  );
}
